import { withTranslation } from 'react-i18next';
import { constant, util } from '@helper';
import { Promise } from 'bluebird';
import React from 'react';
import ReloadIcon from '@rsuite/icons/Reload';
import _ from 'lodash';
import {
  FaHome,
  FaBuilding,
  FaSuitcase,
  FaFileAlt,
  FaMagic,
  FaUser,
  FaEnvelope,
  FaDownload,
  FaSearch,
  FaEye,
  FaClock,
  FaCopy,
} from 'react-icons/fa';

import {
  Col,
  Affix,
  Stack,
  IconButton,
  FlexboxGrid,
  Grid,
  Row,
  Button,
  InputGroup,
  Input,
  SelectPicker,
  toaster,
  Table,
  Pagination,
  Panel,
  Divider,
  Heading,
  HeadingGroup,
  Modal,
  Text,
} from 'rsuite';
import { Icon } from '@rsuite/icons';
import { DocumentAPI, InvoiceAPI } from '@services/api';
import { StatusButton } from '@common/components';
import moment from 'moment';
import AuthStorage from '@common/services/authStorage';
import { RowDataType } from 'rsuite/esm/Table';

const { withRouter } = util;

type State = {
  loading: boolean;
  loadingRefresh: boolean;
  currentPage: number;
  currentLimit: number;
  keyword: string;
  status: string;
  updatedAt: string;
  data: Array<any>;
  total: number;
  invoice?: any;
  expiresIn?: number;

  showPendingModal?: boolean;
};

class CandidateMyCVMakerScreen extends React.Component<any, State> {
  intvl: any;

  constructor(props: any) {
    super(props);

    this.state = {
      loading: false,
      loadingRefresh: false,
      currentPage: 1,
      currentLimit: 10,
      updatedAt: moment().format('DD MMM YYYY, HH:mm:ss'),
      keyword: '',
      status: '',
      data: [],
      total: 0,
    };
  }

  async componentDidMount(): Promise<void> {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    await Promise.allSettled([
      new Promise((resolve, reject) => {
        try {
          this.getInvoices();
          resolve();
        } catch (err) {
          reject(err);
        }
      }),
    ]);
  }

  componentDidUpdate(
    _prevProps: Readonly<any>,
    prevState: Readonly<State>,
  ): void {
    let needRefresh = false;
    if (prevState.status !== _.get(this, 'state.status')) {
      needRefresh = true;
    }

    if (prevState.keyword !== _.get(this, 'state.keyword')) {
      needRefresh = true;
    }

    if (needRefresh) {
      this.getInvoices();
    }
  }

  private copyToClipboard = async (prop: string): Promise<void> => {
    try {
      const invoice = _.get(this, 'state.invoice');
      await navigator.clipboard.writeText(String(_.get(invoice, prop)));

      toaster.push(util.buildInfoMessage('Copied to clipboard!'));
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  private detail = (rowData: RowDataType<any>): void => {
    const expired = new Date(
      `${String(_.get(rowData, 'expiresIn'))}Z`,
    ).getTime();
    const now = new Date().getTime();
    const diff = (expired - now) / 1000;

    if (this.intvl) {
      clearInterval(this.intvl);
      this.setState({ expiresIn: diff });
    }

    if (diff > 0) {
      this.intvl = setInterval(() => {
        const f = new Date().getTime();
        this.setState({ expiresIn: (expired - f) / 1000 });
      }, 1000);
    }

    this.setState({
      showPendingModal: true,
      invoice: rowData,
    });
  };

  private refreshStatus = async (): Promise<void> => {
    try {
      this.setState({ loadingRefresh: true });

      const { invoice } = this.state;
      const resp = await InvoiceAPI.getInvoiceByNumber(
        _.get(invoice, 'number'),
      );
      const status = _.get(resp, 'data.data.status');

      if (status !== 'pending') {
        this.setState(
          {
            showPendingModal: false,
          },
          this.getInvoices,
        );
      }
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({ loadingRefresh: false });
    }
  };

  private redirectTo = (url: string): void => {
    const { navigate } = this.props;
    navigate(url, { replace: true });

    window.location.reload();
  };

  private getInvoices = async (): Promise<void> => {
    try {
      this.setState({ loading: true });

      const { currentPage, currentLimit, keyword, status } = this.state;
      const response = await InvoiceAPI.list(
        currentPage,
        currentLimit,
        keyword,
        status,
      );
      const data = _.get(response, 'data.data') || [];
      const total = Number(_.get(response, 'data.meta.total') || 0);

      this.setState({
        data,
        total,
        updatedAt: moment().format('DD MMM YYYY, HH:mm:ss'),
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  private downloadCV = async (filename: string): Promise<void> => {
    try {
      await DocumentAPI.downloadFile(filename);
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  private sendInvoiceToEmail = async (invoiceNumber: string): Promise<void> => {
    try {
      await InvoiceAPI.sendInvoiceToEmail(invoiceNumber);

      const user = AuthStorage.getInstance().getUser();
      toaster.push(
        util.buildMessage(
          `Successfully sent the resume PDF to your email: ${_.get(
            user,
            'email',
          )}`,
        ),
      );
    } catch (e: any) {
      toaster.push(
        util.buildErrorMessage(_.get(e, 'response.data.message') || e.message),
      );
    }
  };

  private renderPendingModal = ({ t } = this.props): React.ReactNode | null => {
    const invoice = _.get(this.state, 'invoice');
    const additionalInfo = JSON.parse(_.get(invoice, 'additionalInfo') || '{}');

    return (
      <Modal
        size="md"
        className="cv-maker-payment-modal"
        open={_.get(this.state, 'showPendingModal')}
        onClose={() => {
          this.setState({ showPendingModal: false });
        }}
      >
        <Modal.Body>
          <Stack direction="column" spacing={24}>
            <Stack.Item>
              <img src="/icon-cvmaker-pending-payment.png" alt="logo-pending" />
            </Stack.Item>
            <Stack.Item>
              <HeadingGroup className="cvmaker-title-header">
                <Heading>
                  {t(constant.translation.cvMaker.pendingPaymentTitle)}
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.pendingPaymentSubTitle)}
                </Text>
              </HeadingGroup>
            </Stack.Item>
            <Stack.Item>
              <Button
                className="btn-expire"
                appearance="ghost"
                startIcon={<FaClock />}
              >
                {'Expire in: '}
                <b style={{ marginLeft: '.5em' }}>
                  {_.get(this.state, 'expiresIn', 0) > 0 &&
                    util.formatTimeWithHour(
                      _.get(this.state, 'expiresIn') || 0,
                    )}

                  {_.get(this.state, 'expiresIn', 0) <= 0 && 'EXPIRED'}
                </b>
              </Button>
            </Stack.Item>
            <Stack.Item>
              <Divider />
            </Stack.Item>
            <Stack.Item className="w-100">
              <Stack
                direction="row"
                justifyContent="center"
                className={`mb-4 ${
                  _.get(invoice, 'qrUrl') ? 'd-block' : 'd-none'
                }`}
              >
                <Stack.Item>
                  <Stack
                    className="payment-details"
                    direction="column"
                    alignItems="flex-start"
                  >
                    <img
                      src={_.get(invoice, 'qrUrl')}
                      alt="qris"
                      style={{ maxWidth: '25rem' }}
                    />
                  </Stack>
                </Stack.Item>
              </Stack>
              <Stack
                direction="row"
                justifyContent={
                  _.get(invoice, 'vaNumber') ? 'space-between' : 'center'
                }
              >
                <Stack.Item
                  style={{
                    display: _.get(invoice, 'vaNumber') ? 'block' : 'none',
                  }}
                >
                  <Stack
                    className="payment-details"
                    direction="column"
                    alignItems="flex-start"
                  >
                    <p>
                      {_.get(additionalInfo, 'paymentCode') === 'MANDIRI'
                        ? 'Bill Payment'
                        : 'VA Number'}
                      {' - '}
                      {_.get(additionalInfo, 'paymentCode') || '-'}
                    </p>
                    <p className="subtitle">
                      {_.get(this.state, 'invoice.vaNumber')}{' '}
                      <IconButton
                        icon={<FaCopy />}
                        onClick={() => this.copyToClipboard('vaNumber')}
                      />
                    </p>
                  </Stack>
                </Stack.Item>
                <Stack.Item
                  style={{
                    display: _.get(this.state, 'invoice.billerCode')
                      ? 'block'
                      : 'none',
                  }}
                >
                  <Stack
                    className="payment-details"
                    direction="column"
                    alignItems="flex-start"
                  >
                    <p>Biller Code</p>
                    <p className="subtitle">
                      {_.get(this.state, 'invoice.billerCode')}{' '}
                      <IconButton
                        icon={<FaCopy />}
                        onClick={() => this.copyToClipboard('billerCode')}
                      />
                    </p>
                  </Stack>
                </Stack.Item>
                <Stack.Item>
                  <Stack
                    className="payment-details"
                    direction="column"
                    alignItems="flex-start"
                  >
                    <p>Total Payment</p>
                    <p className="subtitle">
                      {util.formatCurrency(_.get(this.state, 'invoice.price'))}{' '}
                      <IconButton
                        icon={<FaCopy />}
                        onClick={() => this.copyToClipboard('price')}
                      />
                    </p>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Divider />
            </Stack.Item>
            <Stack.Item>
              <div className="note-container">
                <p>
                  - Once your payment is confirmed, we'll email your CV and
                  invoice to you.
                </p>
                <p>
                  - If you encounter any issues, feel free to reach out to us at
                  <b> halotalentgrowth@gmail.com</b>. We're happy to help!
                </p>
              </div>
            </Stack.Item>
          </Stack>
        </Modal.Body>
        {_.get(invoice, 'status') === 'pending' &&
          _.get(this.state, 'expiresIn', 0) > 0 && (
            <Modal.Footer>
              <Stack
                spacing={16}
                className="w-100"
                justifyContent="flex-end"
                direction="row"
              >
                <Stack.Item>
                  <Button
                    appearance="subtle"
                    className="btn-tg-fortier"
                    onClick={() => this.setState({ showPendingModal: false })}
                  >
                    {t(constant.translation.general.formCancelButtonKey)}
                  </Button>
                </Stack.Item>
                <Stack.Item>
                  <Button
                    className="next-btn"
                    appearance="primary"
                    onClick={this.refreshStatus}
                    disabled={_.get(this.state, 'loadingRefresh')}
                    startIcon={<ReloadIcon />}
                  >
                    {_.get(this.state, 'loadingRefresh')
                      ? 'Checking...'
                      : t(
                          constant.translation.general.formRefreshStatButtonKey,
                        )}
                  </Button>
                </Stack.Item>
              </Stack>
            </Modal.Footer>
          )}
      </Modal>
    );
  };

  renderMobileCards = (): JSX.Element | null => {
    const { loading, data } = this.state;

    if (loading) {
      return (
        <div className="card-view-loading">
          <Text muted>Loading...</Text>
        </div>
      );
    }

    if (!data.length) {
      return (
        <div className="card-view-empty">
          <Text muted>No invoices found.</Text>
        </div>
      );
    }

    return (
      <div className="invoice-cards">
        {_.map(data, (rowData, idx) => (
          <Panel
            key={_.get(rowData, 'number', idx)}
            bordered
            className="invoice-card"
          >
            {/* Header: invoice number + status */}
            <div className="invoice-card__header">
              <span className="invoice-card__number">
                {_.get(rowData, 'number')}
              </span>
              <StatusButton variant={rowData.status} />
            </div>

            <Divider />

            {/* Details */}
            <div className="invoice-card__details">
              <div className="invoice-card__row">
                <span className="invoice-card__label">Created At</span>
                <span className="invoice-card__value">
                  {_.get(rowData, 'createdAt')}
                </span>
              </div>
              <div className="invoice-card__row">
                <span className="invoice-card__label">Price</span>
                <span className="invoice-card__value">
                  {util.formatCurrency(_.get(rowData, 'price', 0))}
                </span>
              </div>
              <div className="invoice-card__row">
                <span className="invoice-card__label">CV File</span>
                <span className="invoice-card__value">
                  {rowData.file && rowData.status === 'success' ? (
                    <Button
                      appearance="link"
                      onClick={() => window.open(_.get(rowData, 'file.url'))}
                      className="btn-tg-link-btn"
                    >
                      <FaEye className="mr-2" />
                      View
                    </Button>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
            </div>

            {/* Actions */}
            {(rowData.status === 'success' || rowData.status === 'pending') && (
              <>
                <Divider />
                <div className="invoice-card__actions">
                  {rowData.status === 'success' && (
                    <Stack spacing={16} justifyContent="center">
                      <Button
                        appearance="subtle"
                        className="btn-tg-fortier btn-tg-icon-only d-inline"
                        onClick={() => this.sendInvoiceToEmail(rowData.number)}
                        startIcon={<Icon as={FaEnvelope} />}
                      />
                      <Button
                        appearance="primary"
                        className="btn-tg-primary btn-tg-icon-only d-inline"
                        onClick={() =>
                          this.downloadCV(_.get(rowData, 'file.filename'))
                        }
                        startIcon={<Icon as={FaDownload} />}
                      />
                    </Stack>
                  )}
                  {rowData.status === 'pending' && (
                    <Button
                      appearance="subtle"
                      className="btn-tg-fortier d-inline"
                      onClick={() => this.detail(rowData)}
                      style={{ cursor: 'pointer' }}
                    >
                      Pay Now
                    </Button>
                  )}
                </div>
              </>
            )}
          </Panel>
        ))}
      </div>
    );
  };

  renderSideTabs = ({ location } = this.props): JSX.Element => (
    <Col lg={5} md={5} sm={0} xs={0}>
      <Affix className="tg-affix-side-panel" top={window.innerHeight * 0.15}>
        <Stack spacing={6} direction="column">
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/home')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/home')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaHome />}
          >
            Home
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/search-companies')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/search-companies',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaBuilding />}
          >
            Company
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/search-jobs')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/search-jobs')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaSuitcase />}
          >
            Search Job
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/my-application')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/my-application',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaFileAlt />}
          >
            My Application
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/my-cv-maker')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/my-cv-maker')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaMagic />}
          >
            My CV Maker
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/profile')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/profile')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaUser />}
          >
            Profile
          </IconButton>
        </Stack>
      </Affix>
    </Col>
  );

  renderMainContent = (): JSX.Element | null => {
    const { loading, data, total, currentLimit, currentPage, updatedAt } =
      this.state;

    return (
      <Col md={24} sm={24} xs={24} lg={19} className="my-cv-maker-main-content">
        <FlexboxGrid className="w-100">
          <FlexboxGrid.Item className="w-100" as={Col}>
            <Stack alignItems="center" justifyContent="space-between">
              <h1>My CV Maker</h1>
              {/* <Stack spacing={16}>
                <Button
                  appearance="subtle"
                  className="btn-tg-fortier d-inline"
                  onClick={this.sendCVToEmail}
                  startIcon={<Icon as={FaEnvelope} />}
                >
                  {t(constant.translation.general.formSendToEmailButtonKey)}
                </Button>
                <Button
                  appearance="primary"
                  className="btn-tg-primary d-inline"
                  onClick={() => null}
                  startIcon={<Icon as={FaDownload} />}
                >
                  {t(
                    constant.translation.general
                      .formDownloadInvoiceAndCVButtonKey,
                  )}
                </Button>
              </Stack> */}
            </Stack>
            <Stack>
              <div className="d-flex">
                <p className="subtitle">Last update at {updatedAt}</p>
                <IconButton
                  onClick={() => this.getInvoices()}
                  appearance="subtle"
                  className="float-right mt-4 ml-4"
                  icon={<ReloadIcon />}
                />
              </div>
            </Stack>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item className="w-100 mt-8" as={Col}>
            <Stack justifyContent="space-between" className="search-filter-bar">
              <Stack.Item className="search-input-item">
                <InputGroup>
                  <InputGroup.Button>
                    <FaSearch />
                  </InputGroup.Button>
                  <Input
                    name="search"
                    placeholder="Search by invoice number..."
                    onChange={(keyword: string) => this.setState({ keyword })}
                  />
                </InputGroup>
              </Stack.Item>
              <Stack.Item className="status-filter-item">
                <SelectPicker
                  data={_.map(
                    [
                      {
                        name: 'All',
                        value: '',
                      },
                      {
                        name: 'Success',
                        value: 'success',
                      },
                      {
                        name: 'Failed',
                        value: 'failed',
                      },
                      {
                        name: 'Pending',
                        value: 'pending',
                      },
                    ],
                    (l) => ({
                      label: _.get(l, 'name'),
                      value: _.get(l, 'value'),
                    }),
                  )}
                  searchable={false}
                  onSelect={(value) => this.setState({ status: value })}
                  onClean={() => this.setState({ status: '' })}
                  placeholder="Choose the status"
                />
              </Stack.Item>
            </Stack>
            <Stack
              alignItems="flex-start"
              className="mt-4"
              spacing={16}
              direction="column"
            >
              <Button
                appearance="primary"
                className="btn-tg-primary d-inline mt-2"
                onClick={() => this.redirectTo('/cv-maker')}
                startIcon={<Icon as={FaFileAlt} />}
              >
                {' '}
                Build new CV
              </Button>
              <Stack.Item className="w-100 mt-2 table-container-item">
                {/* === Desktop: table view === */}
                <div className="table-view">
                  <div className="table-responsive-wrapper">
                    <Table
                      autoHeight
                      loading={loading}
                      rowHeight={() => 80}
                      data={data}
                      bordered
                      headerHeight={80}
                      onRowClick={() => null}
                    >
                      <Table.Column flexGrow={1} align="center">
                        <Table.HeaderCell>Invoice Number</Table.HeaderCell>
                        <Table.Cell>
                          {(rowData) => {
                            return rowData.number;
                          }}
                        </Table.Cell>
                      </Table.Column>
                      <Table.Column flexGrow={1} align="center">
                        <Table.HeaderCell>Created At</Table.HeaderCell>
                        <Table.Cell>
                          {(rowData) => {
                            return rowData.createdAt;
                          }}
                        </Table.Cell>
                      </Table.Column>
                      <Table.Column flexGrow={1} align="center">
                        <Table.HeaderCell>CV File</Table.HeaderCell>
                        <Table.Cell>
                          {(rowData) => {
                            return rowData.file &&
                              rowData.status === 'success' ? (
                              <Button
                                appearance="link"
                                onClick={() =>
                                  window.open(_.get(rowData, 'file.url'))
                                }
                                className="btn-tg-link-btn"
                              >
                                <FaEye className="mr-2" />
                                View
                              </Button>
                            ) : (
                              '-'
                            );
                          }}
                        </Table.Cell>
                      </Table.Column>
                      <Table.Column flexGrow={1} align="center">
                        <Table.HeaderCell>Price</Table.HeaderCell>
                        <Table.Cell>
                          {(rowData) => {
                            return util.formatCurrency(
                              _.get(rowData, 'price', 0),
                            );
                          }}
                        </Table.Cell>
                      </Table.Column>
                      <Table.Column flexGrow={1} align="center">
                        <Table.HeaderCell>Payment Status</Table.HeaderCell>
                        <Table.Cell>
                          {(rowData) => (
                            <StatusButton variant={rowData.status} />
                          )}
                        </Table.Cell>
                      </Table.Column>
                      <Table.Column flexGrow={1} align="center">
                        <Table.HeaderCell>Action</Table.HeaderCell>
                        <Table.Cell>
                          {(rowData) => {
                            if (rowData.status === 'success') {
                              return (
                                <Stack spacing={16}>
                                  <Button
                                    appearance="subtle"
                                    className="btn-tg-fortier
                                btn-tg-icon-only d-inline"
                                    onClick={() =>
                                      this.sendInvoiceToEmail(rowData.number)
                                    }
                                    startIcon={<Icon as={FaEnvelope} />}
                                  />
                                  <Button
                                    appearance="primary"
                                    className="btn-tg-primary
                                btn-tg-icon-only d-inline"
                                    onClick={() =>
                                      this.downloadCV(
                                        _.get(rowData, 'file.filename'),
                                      )
                                    }
                                    startIcon={<Icon as={FaDownload} />}
                                  />
                                </Stack>
                              );
                            }

                            if (rowData.status === 'pending') {
                              return (
                                <Button
                                  appearance="subtle"
                                  className="btn-tg-fortier d-inline"
                                  onClick={() => this.detail(rowData)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Pay Now
                                </Button>
                              );
                            }

                            return '';
                          }}
                        </Table.Cell>
                      </Table.Column>
                    </Table>
                  </div>
                </div>

                {/* === Mobile: card view === */}
                <div className="card-view">{this.renderMobileCards()}</div>

                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  className="mt-4"
                  size="xs"
                  layout={['total', '-', '|', 'pager']}
                  total={total}
                  limit={currentLimit}
                  activePage={currentPage}
                  onChangePage={(page: number) =>
                    this.setState({ currentPage: page })
                  }
                />
              </Stack.Item>
            </Stack>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Col>
    );
  };

  render(): null | JSX.Element {
    return (
      <>
        {this.renderPendingModal()}
        <Grid className="home-static-authorized-container" fluid>
          <Row>
            {this.renderSideTabs()}
            {this.renderMainContent()}
          </Row>
        </Grid>
      </>
    );
  }
}

export default withRouter(withTranslation()(CandidateMyCVMakerScreen));
